import {FileUploadTargetType} from '@toolify/client/src/modules/files/upload/types/FileUploadTargetType'
import {IResumableUploadRecord} from '@toolify/client/src/modules/files/upload/types/IResumableUploadRecord'

const STORAGE_KEY = 'uploadResumeSessions:v1'
// the server keeps a session for 24h, so a record older than that can never be resumed
const RECORD_TTL_IN_MS = 24 * 60 * 60 * 1000
const MAX_RECORDS = 40

function readRecords(): IResumableUploadRecord[] {
  try {
    const rawValue = window.localStorage?.getItem(STORAGE_KEY)
    if(!rawValue) {
      return []
    }
    const parsed = JSON.parse(rawValue)
    if(!Array.isArray(parsed)) {
      return []
    }
    const oldestAllowedMs = Date.now() - RECORD_TTL_IN_MS
    return parsed.filter((record: IResumableUploadRecord) => {
      return !!record?.uploadSessionId && record.createdAtMs > oldestAllowedMs
    })
  } catch {
    return []
  }
}

function writeRecords(records: IResumableUploadRecord[]): void {
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(records.slice(-MAX_RECORDS)))
  } catch {
    // storage is unavailable in private mode and some embedded webviews; resume is a bonus, not a requirement
  }
}

// the same bytes must end up in the same place, so the target is part of the identity
export function buildUploadTargetKey(targetType: FileUploadTargetType, target: unknown): string {
  return `${targetType}:${JSON.stringify(target ?? null)}`
}

function isSameFile(record: IResumableUploadRecord, file: File): boolean {
  return record.fileName === file.name
    && record.fileSize === file.size
    && record.fileLastModified === file.lastModified
}

export function rememberResumableUpload(record: IResumableUploadRecord): void {
  const records = readRecords().filter(existingRecord => {
    return existingRecord.uploadSessionId !== record.uploadSessionId
      && !(existingRecord.targetKey === record.targetKey && isSameFileRecord(existingRecord, record))
  })
  records.push(record)
  writeRecords(records)
}

function isSameFileRecord(first: IResumableUploadRecord, second: IResumableUploadRecord): boolean {
  return first.fileName === second.fileName
    && first.fileSize === second.fileSize
    && first.fileLastModified === second.fileLastModified
}

export function findResumableUpload(
  file: File,
  targetType: FileUploadTargetType,
  target: unknown,
): IResumableUploadRecord|null {
  const targetKey = buildUploadTargetKey(targetType, target)
  const records = readRecords()
  // newest first: if the same file was started more than once, the latest session is the one worth resuming
  for(let index = records.length - 1; index >= 0; index--) {
    const record = records[index]
    if(record.targetKey === targetKey && isSameFile(record, file)) {
      return record
    }
  }
  return null
}

export function forgetResumableUpload(uploadSessionId: string): void {
  const records = readRecords()
  writeRecords(records.filter(record => {
    return record.uploadSessionId !== uploadSessionId
  }))
}
